import React, { useEffect, useRef } from 'react';
import { animate, splitText, stagger } from 'animejs';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useReducedMotion from 'hooks/useReducedMotion';

export default function ChatAreaMessage({
    message,
    isLastMessage,
}) {

    const ref = useRef();
    const you = message?.role === 'user' || message?.you;
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        // The reveal only enhances an already-visible default; when the user
        // prefers reduced motion (or it's not the latest assistant turn), skip
        // it and the text renders at full opacity immediately.
        if (!isLastMessage || you || reducedMotion) return;
        const { chars } = splitText(ref.current, {
            chars: { wrap: 'clip' },
        });
        animate(chars, {
            opacity: [
                { to: ['0%', '100%'] },
            ],
            duration: 5,
            delay: stagger(15)
        });
    }, [isLastMessage, you, reducedMotion]);

    return (
        <div className={`w-full flex py-4 md:py-6 ${you ? 'justify-end' : 'justify-start'} group`}>
            <div className={`relative max-w-[85%] md:max-w-[75%] ${you ? 'ml-auto' : 'mr-auto'}`}>
                <div
                    className={`text-[15px] md:text-base leading-relaxed ${
                        you
                            ? 'bg-black text-white py-3 px-4 md:py-4 md:px-5 rounded-3xl rounded-br-md'
                            : 'text-black'
                    }`}
                >
                    <div ref={ref} className='prose prose-sm md:prose-base max-w-none'>
                        <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                p: ({ children, ...props }) => (
                                    <p className={`${you ? 'text-white' : 'text-black'} mb-3 last:mb-0`} {...props}>
                                        {children}
                                    </p>
                                ),
                                ul: ({ children, ...props }) => (
                                    <ul className={`${you ? 'text-white' : 'text-black'} list-disc pl-5 mb-3 space-y-1`} {...props}>
                                        {children}
                                    </ul>
                                ),
                                ol: ({ children, ...props }) => (
                                    <ol className={`${you ? 'text-white' : 'text-black'} list-decimal pl-5 mb-3 space-y-1`} {...props}>
                                        {children}
                                    </ol>
                                ),
                                li: ({ children, ...props }) => (
                                    <li className={`${you ? 'text-white' : 'text-black'}`} {...props}>
                                        {children}
                                    </li>
                                ),
                                code: ({ inline, children, ...props }) =>
                                    inline ? (
                                        <code
                                            {...props}
                                            className={`${
                                                you ? 'bg-white/20 text-white' : 'bg-black/5 text-black'
                                            } px-1.5 py-0.5 rounded text-sm font-mono`}
                                        >
                                            {children}
                                        </code>
                                    ) : (
                                        <code
                                            {...props}
                                            className={`block ${
                                                you ? 'bg-white/10 text-white' : 'bg-black/5 text-black'
                                            } p-4 rounded-lg overflow-x-auto font-mono text-sm my-3`}
                                        >
                                            {children}
                                        </code>
                                    ),
                                pre: ({ children, ...props }) => (
                                    <pre className="my-3" {...props}>
                                        {children}
                                    </pre>
                                ),
                                blockquote: ({ children, ...props }) => (
                                    <blockquote
                                        className={`${
                                            you ? 'border-white/30 text-white/90' : 'border-black/20 text-black/80'
                                        } border-l-4 pl-4 my-3 italic`}
                                        {...props}
                                    >
                                        {children}
                                    </blockquote>
                                ),
                                a: ({ children, ...props }) => (
                                    <a
                                        className={`${
                                            you ? 'text-white underline' : 'text-blue-600 hover:text-blue-800'
                                        } underline`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        {...props}
                                    >
                                        {children}
                                    </a>
                                ),
                                strong: ({ children, ...props }) => (
                                    <strong className="font-semibold" {...props}>
                                        {children}
                                    </strong>
                                ),
                                em: ({ children, ...props }) => (
                                    <em className="italic" {...props}>
                                        {children}
                                    </em>
                                ),
                            }}
                        >
                            {message.content}
                        </Markdown>
                    </div>
                </div>
            </div>
        </div>
    )
}
